import React from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Typography, Stack } from '@mui/material';
import SectionWrapper from '../SectionWrapper';

/**
 * OVERVIEW EXPERTISE COMPONENT - V8 (HARMONY)
 * Concept: "Strategic Portfolio".
 * Layout: 3-Column Grid (Balance).
 */
const OverviewExpertise = ({ engines, sectionTitle }) => {
    return (
        <SectionWrapper
            id="engines-genesis"
            className="section-expertise"
            sx={{
                bgcolor: '#050505',
                color: '#fff',
                // This section must feel "perfect fit" (no inner scrollbar).
                '& .section-body': {
                    // On narrow/small viewports, allow internal scroll instead of forcing squeeze.
                    overflowY: { xs: 'auto', md: 'hidden' },
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                },
            }}
        >
            <Container
                maxWidth={false}
                sx={{
                    maxWidth: '1400px',
                    mx: 'auto',
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    // Fixed-height section: keep spacing responsive to viewport height.
                    py: 'clamp(20px, 4.5vmin, 56px)',
                    px: { xs: 2, md: 0 },
                    '@media (max-height: 820px)': {
                        py: 'clamp(14px, 3.0vmin, 34px)',
                    },
                }}
            >
                <Box
                    sx={{
                        mb: 'clamp(18px, 3.8vmin, 56px)',
                        textAlign: 'left',
                        borderBottom: '1px solid #333',
                        pb: 'clamp(12px, 2.2vmin, 28px)',
                        '@media (max-height: 820px)': {
                            mb: 'clamp(12px, 2.2vmin, 24px)',
                            pb: 'clamp(10px, 1.8vmin, 18px)',
                        },
                    }}
	                >
	                    <Typography variant="overline" sx={{ fontFamily: '"JetBrains Mono", monospace', color: '#666', letterSpacing: '0.2em', fontWeight: 600, display: 'block', mb: 1 }}>
	                        {'// SYSTEM_CAPABILITIES'}
	                    </Typography>
                    <Typography
                        variant="h2"
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            // Avoid overflowing on short viewports
                            fontSize: 'clamp(2.1rem, 5.2vmin, 4rem)',
                            fontWeight: 700,
                            letterSpacing: '-0.04em',
                            color: '#fff',
                            lineHeight: 1.05
                        }}
                    >
                        {sectionTitle}<span style={{ color: '#CC0000' }}>.</span>
                    </Typography>
                </Box>

                {/* THE ENGINE BLOCK (FUSED GRID) */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
                    gridAutoRows: { xs: 'auto', md: 'minmax(0, 1fr)' },
                    gap: { xs: 6, md: 0 }, // mobile separation prevents visual stacking/collision
                    border: '1px solid #333',
                    borderRadius: '14px', // Industrial Square
                    // Cards with fixed, slightly smaller desktop height to avoid bottom overflow.
                    height: { xs: 'auto', md: 'clamp(430px, 58vh, 560px)' },
                    flex: { xs: 'unset', md: '0 0 auto' },
                    minHeight: 0,
                    overflow: { xs: 'visible', md: 'hidden' },
                    alignContent: { xs: 'start', md: 'stretch' }
                }}>
                    {engines.map((engine, index) => (
                        <Box
                            key={engine.id}
                            sx={{
                                // Card-level responsiveness: spacing adapts to both width and height.
                                p: 'clamp(12px, 1.45vmin, 20px)',
                                height: { xs: 'auto', md: '100%' },
                                bgcolor: '#0A0A0A',
                                borderRight: { md: '1px solid #333' },
                                borderBottom: { xs: '1px solid #333', md: 'none' },
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)',
                                overflow: { xs: 'visible', md: 'hidden' },
                                minHeight: 0,
                                '@media (max-height: 820px)': {
                                    p: 'clamp(10px, 1.2vmin, 14px)',
                                },
                                '&:hover': {
                                    bgcolor: '#CC0000', // IGNITION
                                    '& .engine-number': { color: 'rgba(255,255,255,0.2)' },
                                    '& .engine-title': { color: '#fff', transform: 'translateX(10px)' },
                                    '& .engine-desc': { color: 'rgba(255,255,255,0.9)' },
                                    '& .cap-item': { color: '#fff', '&::before': { bgcolor: '#fff' } },
                                    '& .icon-box': { color: '#fff', borderColor: '#fff' }
                                }
                            }}
                        >
                            {/* BACKGROUND NUMBER (Industrial Watermark) */}
                            <Typography
                                className="engine-number"
                                sx={{
                                    position: 'absolute',
                                    top: 'clamp(10px, 1.4vmin, 20px)',
                                    right: 'clamp(10px, 1.4vmin, 20px)',
                                    fontSize: { xs: 'clamp(3.8rem, 16vw, 5.4rem)', md: 'clamp(4.8rem, 8.2vmin, 7.4rem)' },
                                    fontWeight: 900,
                                    color: '#1a1a1a',
                                    lineHeight: 0.8,
                                    zIndex: 0,
                                    transition: 'all 0.4s ease',
                                    fontFamily: '"JetBrains Mono", monospace' // Technical font for number
                                }}>
                                {engine.number}
                            </Typography>

                            {/* ICON */}
                            <Box
                                className="icon-box"
                                sx={{
                                    width: 'clamp(40px, 4.4vmin, 54px)',
                                    height: 'clamp(40px, 4.4vmin, 54px)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#CC0000',
                                    border: '1px solid #333',
                                    borderRadius: '14px', // Square Icon
                                    mb: 'clamp(10px, 1.8vmin, 24px)',
                                    position: 'relative',
                                    zIndex: 1,
                                    transition: 'all 0.4s ease'
                                }}
                            >
                                {engine.icon}
                            </Box>

                            {/* CONTENT */}
                            {/* On small screens we don't want a forced "tall card" with an empty gap.
                               On md+ the footer can stick to the bottom for alignment across columns. */}
                            <Box sx={{ position: 'relative', zIndex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <Typography
                                    className="engine-title"
                                    variant="h4"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 'clamp(6px, 0.8vmin, 10px)',
                                        // Whole-card responsive title scale (balanced for width + height).
                                        fontSize: {
                                            xs: 'clamp(1rem, 2.6vw, 1.22rem)',
                                            sm: 'clamp(1.08rem, 2.1vw, 1.34rem)',
                                            md: 'clamp(1.16rem, 0.72vw + 0.72rem, 1.62rem)',
                                            lg: 'clamp(1.24rem, 0.68vw + 0.76rem, 1.88rem)',
                                            xl: 'clamp(1.34rem, 0.64vw + 0.82rem, 2.08rem)'
                                        },
                                        color: '#fff',
                                        letterSpacing: '-0.02em',
                                        transition: 'transform 0.4s ease',
                                        fontFamily: '"Inter", sans-serif'
                                    }}>
                                    {engine.title}
                                </Typography>

                                <Typography
                                    className="engine-desc"
                                    variant="body1"
                                    sx={{
                                        color: '#888',
                                        lineHeight: 1.45,
                                        mb: 'clamp(8px, 1vmin, 12px)',
                                        // Description scales with the card to avoid overlap/crowding.
                                        fontSize: {
                                            xs: 'clamp(0.88rem, 1.7vw + 0.54rem, 0.98rem)',
                                            sm: 'clamp(0.92rem, 1.15vw + 0.58rem, 1.04rem)',
                                            md: 'clamp(0.92rem, 0.26vw + 0.66rem, 1.02rem)',
                                            lg: 'clamp(0.96rem, 0.28vw + 0.68rem, 1.08rem)',
                                            xl: 'clamp(1rem, 0.24vw + 0.7rem, 1.12rem)'
                                        },
                                        maxWidth: { xs: '96%', md: '92%' },
                                        transition: 'color 0.4s ease',
                                        fontFamily: '"Inter", sans-serif',
                                        // No ellipsis: show full text. Keep bullets aligned via larger fixed description slot.
                                        display: 'block',
                                        overflowY: { xs: 'visible', md: 'auto' },
                                        overflowX: 'hidden',
                                        minHeight: { md: '12.2em' },
                                        maxHeight: { md: '12.2em' },
                                        pr: { md: 0.6 },
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: 'rgba(255,255,255,0.24) transparent',
                                        '&::-webkit-scrollbar': {
                                            width: '5px'
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'transparent'
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: 'rgba(255,255,255,0.24)'
                                        },
                                        '@media (max-height: 820px)': {
                                            mb: 'clamp(6px, 0.85vmin, 10px)',
                                            minHeight: '10.4em',
                                            maxHeight: '10.4em'
                                        }
                                    }}>
                                    {engine.description}
                                </Typography>
                            </Box>

                            {/* FOOTER: SPECS */}
                            <Box
                                sx={{
                                    // Footer locks to card bottom for horizontal alignment.
                                    mt: 'auto',
                                    pt: 'clamp(8px, 1.2vmin, 14px)',
                                    borderTop: '1px solid rgba(255,255,255,0.1)',
                                    width: '100%',
                                    position: 'relative',
                                    zIndex: 1
                                }}
                            >
                                <Stack direction="column" gap={1}>
                                    {engine.capabilities.map((cap) => (
                                        <Typography
                                            key={cap}
                                            className="cap-item"
                                            variant="caption"
                                            sx={{
                                                color: '#666',
                                                fontWeight: 500,
                                                fontSize: {
                                                    xs: 'clamp(0.74rem, 1.45vw + 0.5rem, 0.84rem)',
                                                    sm: 'clamp(0.78rem, 0.95vw + 0.52rem, 0.9rem)',
                                                    md: 'clamp(0.82rem, 0.28vw + 0.62rem, 0.98rem)',
                                                    lg: 'clamp(0.88rem, 0.26vw + 0.66rem, 1.06rem)',
                                                    xl: 'clamp(0.94rem, 0.24vw + 0.68rem, 1.14rem)'
                                                },
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.15,
                                                transition: 'all 0.4s ease',
                                                fontFamily: '"JetBrains Mono", monospace',
                                                '&::before': {
                                                    content: '""',
                                                    width: 4, height: 4,
                                                    bgcolor: '#CC0000', // Red Dot default
                                                    transition: 'all 0.4s ease'
                                                }
                                            }}
                                        >
                                            {cap}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Container>
        </SectionWrapper>
    );
};

OverviewExpertise.propTypes = {
    sectionTitle: PropTypes.string,
    engines: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        number: PropTypes.string.isRequired,
        icon: PropTypes.element.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        capabilities: PropTypes.arrayOf(PropTypes.string).isRequired,
    })).isRequired,
};

export default OverviewExpertise;
